import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Sparkles,
  Quote,
  Share2,
  FileText,
  Layers,
  Loader2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import useDocumentMeta from "@/hooks/useDocumentMeta";

function Section({ title, children, testId }) {
  if (!children) return null;
  return (
    <section className="mt-12" data-testid={testId}>
      <h2 className="text-[0.72rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
        {title}
      </h2>
      <div className="mt-4 text-[1.05rem] md:text-[1.1rem] leading-[1.85] font-light text-[#0B1E36]/90 whitespace-pre-line">
        {children}
      </div>
    </section>
  );
}

function DerivativeCard({ icon: Icon, label, text, testId }) {
  if (!text) return null;
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#C5A059]/30 p-6 md:p-7 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-12 bg-[#C5A059]" />
      <div className="flex items-center gap-3 text-[#C5A059]">
        <Icon size={16} strokeWidth={1.5} />
        <span className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
          {label}
        </span>
      </div>
      <p className="mt-4 text-[1rem] md:text-[1.05rem] leading-[1.75] font-light text-[#0B1E36]/90 whitespace-pre-line">
        {text}
      </p>
    </div>
  );
}

export default function SermonDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [generating, setGenerating] = useState(false);

  // SEO, meta tags + JSON-LD dinâmicos
  const sermonUrl = s?.slug
    ? `https://cemedeiros.com.br/pregacoes/${s.slug}`
    : undefined;
  useDocumentMeta(
    s
      ? {
          title: `${s.title}, Carlos Medeiros`,
          description:
            (s.preview || s.theme || `Sermão de Carlos Medeiros sobre ${s.category}.`).slice(0, 200),
          canonical: sermonUrl,
          url: sermonUrl,
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: s.title,
            description: s.preview || s.theme,
            url: sermonUrl,
            inLanguage: "pt-BR",
            articleSection: s.category,
            datePublished: s.date,
            author: {
              "@type": "Person",
              name: "Carlos Medeiros",
              url: "https://cemedeiros.com.br/",
            },
            publisher: {
              "@type": "Person",
              name: "Carlos Medeiros",
              url: "https://cemedeiros.com.br/",
            },
          },
        }
      : {}
  );

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api
      .get(`/sermon/${id}`)
      .then(({ data }) => setS(data))
      .catch(() => setErr("Sermão não encontrado."))
      .finally(() => setLoading(false));
  }, [id]);

  const generateDerivatives = useCallback(async () => {
    if (!s) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/sermon/${s.slug || s.id}/derivatives`);
      setS(data);
    } catch (_) {
      // keep silently, button will stay enabled
    } finally {
      setGenerating(false);
    }
  }, [s]);

  const hasDerivatives =
    s?.derivatives &&
    (s.derivatives.devotional ||
      s.derivatives.daily_message ||
      s.derivatives.impact_phrase ||
      s.derivatives.application_phrase);

  return (
    <div data-testid="sermon-page" className="bg-[#FAF8F4] min-h-screen">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <Link
            to="/#pregacoes"
            data-testid="sermon-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059] transition-colors duration-300"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Voltar à biblioteca
          </Link>

          {loading && (
            <div className="mt-16 space-y-6 animate-pulse">
              <div className="h-6 bg-[#0B1E36]/5 w-1/3" />
              <div className="h-12 bg-[#0B1E36]/5 w-3/4" />
              <div className="h-4 bg-[#0B1E36]/5 w-1/2" />
              <div className="h-4 bg-[#0B1E36]/5 w-full mt-10" />
              <div className="h-4 bg-[#0B1E36]/5 w-full" />
              <div className="h-4 bg-[#0B1E36]/5 w-5/6" />
            </div>
          )}

          {!loading && err && (
            <div className="mt-24 text-center text-[#0B1E36]/70">{err}</div>
          )}

          {!loading && s && (
            <>
              <article className="mt-12" data-testid="sermon-article">
                <div className="flex items-center gap-3">
                  <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                    {s.category}
                  </span>
                  {s.number && (
                    <span className="text-[0.7rem] tracking-[0.2em] text-[#0B1E36]/40 tabular-nums">
                      · #{String(s.number).padStart(3, "0")}
                    </span>
                  )}
                </div>

                <h1
                  data-testid="sermon-title"
                  className="mt-5 font-serif-display text-[2.5rem] sm:text-5xl md:text-[3.5rem] lg:text-[4rem] text-[#0B1E36] leading-[1.03] tracking-[-0.028em] font-medium"
                >
                  {s.title}
                </h1>

                {s.series && <SeriesBadge series={s.series} />}

                {s.verse_reference && (
                  <div className="mt-8 border-l-2 border-[#C5A059] pl-5">
                    <p className="text-[0.72rem] uppercase tracking-[0.28em] text-[#C5A059]">
                      Texto base
                    </p>
                    <p className="mt-2 font-serif-display italic text-[1.2rem] md:text-[1.35rem] text-[#0B1E36] leading-[1.5]">
                      {s.verse_reference}
                    </p>
                    {s.verse_text && (
                      <p className="mt-3 text-[0.98rem] text-[#0B1E36]/70 leading-[1.75] font-light">
                        “{s.verse_text}”
                      </p>
                    )}
                  </div>
                )}

                {s.source && (
                  <p className="mt-6 text-[0.72rem] uppercase tracking-[0.22em] text-[#0B1E36]/45">
                    <FileText size={11} className="inline -mt-0.5 mr-1.5" strokeWidth={1.5} />
                    {s.source}
                  </p>
                )}

                <Section
                  title="Introdução"
                  testId="sermon-section-introduction"
                >
                  {s.introduction}
                </Section>
                <Section
                  title="Desenvolvimento"
                  testId="sermon-section-development"
                >
                  {s.development}
                </Section>
                <Section
                  title="Aplicação"
                  testId="sermon-section-application"
                >
                  {s.application}
                </Section>
                <Section
                  title="Conclusão"
                  testId="sermon-section-conclusion"
                >
                  {s.conclusion}
                </Section>

                {/* Fallback: if structured sections missing, show full content */}
                {!s.introduction &&
                  !s.development &&
                  !s.application &&
                  !s.conclusion &&
                  s.content && (
                    <div
                      data-testid="sermon-full-content"
                      className="mt-12 text-[1.05rem] leading-[1.85] font-light text-[#0B1E36]/90 whitespace-pre-line"
                    >
                      {s.content}
                    </div>
                  )}
              </article>

              {/* Derivatives section */}
              <section
                data-testid="sermon-derivatives"
                className="mt-20 border-t border-[#0B1E36]/10 pt-14"
              >
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} strokeWidth={1.5} className="text-[#C5A059]" />
                      <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                        Derivados pastorais
                      </span>
                    </div>
                    <h2 className="mt-3 font-serif-display text-3xl md:text-4xl text-[#0B1E36] tracking-[-0.02em]">
                      Leve este sermão <span className="italic">além do púlpito</span>.
                    </h2>
                    <p className="mt-4 text-[1rem] text-[#6B7280] leading-[1.8] font-light max-w-xl">
                      Em um clique, gere um devocional, uma frase de impacto,
                      uma mensagem para suas redes e um resumo pastoral, tudo
                      fiel ao conteúdo original.
                    </p>
                  </div>
                  <button
                    onClick={generateDerivatives}
                    disabled={generating}
                    data-testid="sermon-generate-derivatives"
                    className="btn-primary-gold shrink-0"
                  >
                    {generating ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Gerando…
                      </>
                    ) : hasDerivatives ? (
                      <>
                        <Sparkles size={14} strokeWidth={1.5} />
                        Gerar novamente
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} strokeWidth={1.5} />
                        Gerar derivados
                      </>
                    )}
                  </button>
                </div>

                {hasDerivatives && (
                  <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DerivativeCard
                      icon={BookOpen}
                      label="Devocional"
                      text={s.derivatives.devotional}
                      testId="sermon-derivative-devotional"
                    />
                    <DerivativeCard
                      icon={Quote}
                      label="Frase de impacto"
                      text={s.derivatives.impact_phrase}
                      testId="sermon-derivative-impact"
                    />
                    <DerivativeCard
                      icon={Share2}
                      label="Mensagem do dia"
                      text={s.derivatives.daily_message || s.derivatives.social_message}
                      testId="sermon-derivative-daily"
                    />
                    <DerivativeCard
                      icon={FileText}
                      label="Aplicação prática"
                      text={s.derivatives.application_phrase || s.derivatives.pastoral_summary}
                      testId="sermon-derivative-application"
                    />
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SeriesBadge({ series }) {
  if (!series || !series.slug) return null;
  const current = series.current_episode || 0;
  const total = series.total_episodes || series.sermon_count || 0;
  const counter = current && total ? `${current}/${total}` : null;

  return (
    <div
      data-testid="sermon-series-badge"
      className="mt-7 bg-white border border-[#C5A059]/30 px-5 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5"
    >
      <div className="flex items-center gap-3 text-[#C5A059] shrink-0">
        <Layers size={14} strokeWidth={1.7} />
        <span className="text-[0.62rem] uppercase tracking-[0.28em] font-medium">
          Faz parte da série
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <Link
          to={`/series/${series.slug}`}
          data-testid="sermon-series-link"
          className="font-serif-display text-[1.05rem] md:text-[1.2rem] text-[#0B1E36] hover:text-[#C5A059] transition-colors duration-300 leading-[1.3] line-clamp-2"
        >
          {series.title}
        </Link>
        {counter && (
          <p className="mt-1 text-[0.72rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 tabular-nums">
            Episódio {counter}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 mt-1 md:mt-0">
        {series.prev_episode && (
          <Link
            to={`/pregacoes/${series.prev_episode.slug}`}
            data-testid="sermon-series-prev"
            title={series.prev_episode.title}
            aria-label="Episódio anterior"
            className="inline-flex items-center justify-center w-9 h-9 border border-[#0B1E36]/15 text-[#0B1E36]/70 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors duration-300"
          >
            <ArrowLeft size={14} strokeWidth={1.6} />
          </Link>
        )}
        {series.next_episode && (
          <Link
            to={`/pregacoes/${series.next_episode.slug}`}
            data-testid="sermon-series-next"
            title={series.next_episode.title}
            aria-label="Próximo episódio"
            className="inline-flex items-center justify-center w-9 h-9 border border-[#0B1E36]/15 text-[#0B1E36]/70 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors duration-300"
          >
            <ArrowRight size={14} strokeWidth={1.6} />
          </Link>
        )}
        <Link
          to={`/series/${series.slug}`}
          data-testid="sermon-series-cta"
          className="hidden sm:inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] text-[#0B1E36] hover:text-[#C5A059] border border-[#0B1E36]/20 hover:border-[#C5A059] px-3 py-2 transition-colors duration-300"
        >
          Ver série
        </Link>
      </div>
    </div>
  );
}

